import { ActivityInformationHotelModel } from '../api/useActivityInformationHotelQuery';
import { Box, Flex, Heading, Link, Text, useDisclosure } from '@chakra-ui/react';
import { InformationAlertDisplay } from './informationAlertDisplay';
import { getFullAddressString } from '../../../../util/formatters/addressFormatters';
import { LabelTextDisplay } from '../../../../components/Common/LabelTextDisplay';
import { faDownload } from '@fortawesome/free-solid-svg-icons';
import { PropsWithChildren, useMemo } from 'react';
import { BaseAlert, BaseCard, BaseDialog, BaseToolbarButton } from '@invara-dev/react-base';
import { downloadFile } from '../../../../util/helpers/fileHelper.ts';
import { ContactInfoBar } from '../../../../components/ContactInfoBar/ContactInfoBar.tsx';
import { EntityNotesDisplay } from '../../../../features/entityNotes/EntityNotesDisplay';
import { useEntityNotesQuery } from '../../../../features/entityNotes/apis/useEntityNotesQuery';
import { EntityNoteQueryType } from '../../../../enums/common/EntityNoteQueryType';
import { EntityNoteType } from '../../../../enums/common/EntityNoteType';
import { ApiEndpoints } from '../../../../constants/endpoints/apiEndpoints.ts';
import HotelRatesText from '../../../../components/DataDisplays/HotelRatesText.tsx';

type Props = {
  val: ActivityInformationHotelModel;
  hotelId?: number;
} & PropsWithChildren;
/**
 * HotelInfoCard component renders detailed information for a single hotel entry.
 *
 * Responsibilities:
 * - Converts raw hotel contact fields into structured props for <ContactInfoBar>.
 * - Displays an error alert at the top if the hotel is marked as unpreferred (`FrontierUnPreferred`).
 * - Shows hotel name, full address, and a contact bar with email, phone, fax, and website links.
 * - Renders various hotel detail fields using <LabelTextDisplay>, such as booking methods, payment notes,
 *   cancellation policy, GDS codes, and custom payments with download links.
 * - Displays Code Red and Agent Notes alerts at the bottom.
 * - Renders any passed-in children below the notes.
 *
 * @component
 * @param {{
 *   val: ActivityInformationHotelModel;  // Hotel data model containing all display fields
 * }} props - Component props
 * @param {React.ReactNode} [props.children] - Optional nested elements to render after hotel notes
 * @returns {JSX.Element} A card displaying comprehensive hotel information.
 */

export const HotelInfoCard = ({ val, children, hotelId }: Props) => {
  const notesDialogDisclosure = useDisclosure();

  // Query to check if notes exist
  const notesQuery = useEntityNotesQuery(
    {
      EntityNoteQueryType: EntityNoteQueryType.Hotel,
      Active: true,
      EntityNoteTypes: [
        EntityNoteType.AgentNote,
        EntityNoteType.Hotel,
        EntityNoteType.CodeRed,
        EntityNoteType.Basic,
        EntityNoteType.Frontier,
        EntityNoteType.Issue
      ],
      IsPinned: true,
      Search: ''
    },
    hotelId
  );

  const convertedContactInfo = useMemo(() => {
    return {
      emails: val.Email ? [{ Address: val.Email, Description: 'Email Address' }] : [],
      phoneNumbers: val.PhoneNumber ? [{ Number: val.PhoneNumber, Description: 'Phone Number' }] : [],
      faxNumbers: val.FaxNumber ? [val.FaxNumber] : [],
      website: val.Website ? { url: val.Website, description: 'Website' } : undefined
    };
  }, [val.Email, val.PhoneNumber, val.FaxNumber, val.Website]);

  return (
    <BaseCard
      variant={'subtle'}
      height={'fit-content'}
      header={
        <Heading>
          {val.FrontierUnPreferred && (
            <BaseAlert status={'error'}>
              <Box fontSize={'14px'}>
                <Box fontWeight={'bold'} display={'inline'}>
                  Frontier Un-preferred:{' '}
                </Box>
                {val.PreferenceNotes}
              </Box>
            </BaseAlert>
          )}
          <Box>{val.HotelName}</Box>
          <Box fontSize={'xs'}>{getFullAddressString(val.Address)}</Box>
          <Box>
            <ContactInfoBar
              emails={convertedContactInfo.emails}
              phoneNumbers={convertedContactInfo.phoneNumbers}
              faxNumbers={convertedContactInfo.faxNumbers}
              website={convertedContactInfo.website}
            />
          </Box>
        </Heading>
      }>
      <Flex flexDir={'column'} alignItems={'flex-start'} marginBottom={'4px'}>
        <LabelTextDisplay label={'Website Notes'} text={val.WebsiteNotes} hideIfTextNull={true} lineClamp={3} />
        <LabelTextDisplay label={'Booking Method'} text={val.BookingMethod} hideIfTextNull={true} lineClamp={3} />
        <LabelTextDisplay
          label={'Booking Method Notes'}
          text={val.BookingMethodNotes}
          hideIfTextNull={true}
          lineClamp={3}
        />

        <LabelTextDisplay label={'Payment Notes'} text={val.PaymentNotes} hideIfTextNull={true} lineClamp={3} />
        <LabelTextDisplay label={'Payment Email'} text={val.PaymentEmail} hideIfTextNull={true} lineClamp={3} />
        <LabelTextDisplay
          label={'Sertifi Canary Notes'}
          text={val.RequiresSertifiCanaryNotes}
          hideIfTextNull={true}
          lineClamp={3}
        />
        <LabelTextDisplay label={'CXL Policy'} text={val.CancellationPolicy} hideIfTextNull={true} lineClamp={3} />
        <LabelTextDisplay label={'Chain Code'} text={val.ChainCode} hideIfTextNull={true} lineClamp={3} />
        <LabelTextDisplay label={'GDS Id'} text={val.GdsId} hideIfTextNull={true} lineClamp={3} />
        <LabelTextDisplay label={'Non GDS Id'} text={val.NonGdsId} hideIfTextNull={true} lineClamp={3} />
        <LabelTextDisplay label={'Notes'} text={val.Notes.join('.')} hideIfTextNull={true} lineClamp={3} />
        {val.FrontierPreferred && (
          <LabelTextDisplay
            label={'Frontier Preference Notes'}
            text={val.PreferenceNotes}
            hideIfTextNull={true}
            lineClamp={3}
          />
        )}
        {val.CustomPayments && val.CustomPayments.length > 0 && (
          <>
            <LabelTextDisplay label={'Custom Payments'} lineClamp={3} />
            <Box>
              {val.CustomPayments.map((x) => (
                <Box key={`${x.FileHandle}-PaymentInformationList`}>
                  <Text textWrap={'wrap'}>{x.Notes + ' '}</Text>
                  <BaseToolbarButton toolTip={'Download File'} icon={faDownload}>
                    <Link
                      onClick={async () => {
                        if (x.FileHandle) {
                          await downloadFile(ApiEndpoints.dataAccess.getFile(x.FileHandle), x.FileName);
                        }
                      }}>
                      {x.FileName}
                    </Link>
                  </BaseToolbarButton>
                </Box>
              ))}
            </Box>
          </>
        )}
        <HotelRatesText noSpace={true} hotel={val} length={val.RoomTypes.length} />
      </Flex>
      {/* Legacy Alert Display for Quick Reference */}
      <Box>
        <InformationAlertDisplay toDisplay={val.CodeRedNotes} status={'error'} header={'Code Red'} />
      </Box>
      <Box>
        <InformationAlertDisplay toDisplay={val.AgentNotes} status={'warning'} header={'Agent Notes'} />
      </Box>

      {hotelId && notesQuery.data && notesQuery.data.length > 0 && (
        <Box cursor='pointer' onClick={notesDialogDisclosure.onOpen} _hover={{ opacity: 0.8 }} marginTop={'4px'}>
          <InformationAlertDisplay
            toDisplay={['Click to view all hotel notes']}
            status={'info'}
            header={'Hotel Notes'}
          />
        </Box>
      )}

      {/* Draggable Notes Dialog */}
      {hotelId && (
        <BaseDialog
          dragDialog={{
            initialW: 800,
            initialH: 500
          }}
          disclosure={notesDialogDisclosure}>
          <EntityNotesDisplay
            entityId={hotelId}
            queryType={EntityNoteQueryType.Hotel}
            isReadOnly={true}
            isPinned
            noteDisplayTypes={[
              EntityNoteType.AgentNote,
              EntityNoteType.Hotel,
              EntityNoteType.CodeRed,
              EntityNoteType.Basic,
              EntityNoteType.Frontier,
              EntityNoteType.Issue
            ]}
          />
        </BaseDialog>
      )}

      {children}
    </BaseCard>
  );
};
